import { translate } from "../locales/i18n";
import { toastMessage } from "./common";

export class VoiceRecorderButton {
  private rootElement: HTMLDivElement;
  public button: HTMLButtonElement;
  private audioVisualizer: AudioVisualizer;
  private isRecording = false;
  private mediaRecorder: MediaRecorder | null = null;
  private audioChunks: Blob[] = [];
  private stream: MediaStream | null = null;
  private audioStartTime: number | null = null;
  public audioDuration: number = 0;
  public audioFile: File | null = null;

  constructor(private onRecordingFinished: (file: File, duration: number) => Promise<void>) {
    this.rootElement = document.createElement("div");
    this.button = document.createElement("button");
    this.audioVisualizer = new AudioVisualizer();

    this.rootElement.appendChild(this.audioVisualizer.getCanvasElement());
    this.rootElement.appendChild(this.button);

    this.rootElement.classList.add("audio-recorder-container", "flex", "flex-row");

    this.rootElement.style.alignItems = "baseline";

    this.button.classList.add("btn", "btn-primary", "audio-button");
    this.button.type = "button";
    this.button.title = translate("record-audio");
    this.button.innerHTML = '<i class="fa fa-microphone"></i>';

    this.button.addEventListener("click", async () => {
      try {
        if (this.isRecording) {
          this.stopRecording();
        } else {
          await this.startRecording();
        }
      } catch (error) {
        console.error("Fehler bei der Audioaufnahme:", error);
        toastMessage("Audioaufnahme fehlgeschlagen.", "error");
      }
    });
  }

  public getElement(): HTMLDivElement {
    return this.rootElement;
  }

  public setIsLoading(loading: boolean): void {
    if (loading) {
      this.button.innerHTML = '<i class="fa fa-spinner fa-spin"></i>';
      return;
    }
    this.button.innerHTML = '<i class="fa fa-microphone"></i>';
  }

  private async startRecording(): Promise<void> {
    try {
      if (!navigator.mediaDevices || !window.MediaRecorder) {
        throw new Error("Audioaufnahme wird nicht unterstützt.");
      }

      this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.audioChunks = [];

      // Start visualizer
      this.audioVisualizer.start(this.stream);

      this.mediaRecorder = new MediaRecorder(this.stream);
      this.mediaRecorder.ondataavailable = (event) => {
        this.audioChunks.push(event.data);
      };

      this.mediaRecorder.onstop = () => {
        this.audioDuration = this.audioStartTime
          ? Math.max(Math.round((Date.now() - this.audioStartTime) / 1000), 1)
          : 0;
        this.audioStartTime = null;

        const audioBlob = new Blob(this.audioChunks, { type: "audio/webm;codecs=opus" });
        this.audioFile = new File([audioBlob], "recording.webm", {
          type: "audio/webm;codecs=opus",
        });

        // free microphone resources
        this.stream?.getTracks().forEach((track) => track.stop());
        this.stream = null;

        // Stop visualizer
        this.audioVisualizer.stop();

        this.onRecordingFinished(this.audioFile, this.audioDuration);
      };

      this.audioStartTime = Date.now();
      this.mediaRecorder.start();
      this.isRecording = true;
      this.button.innerHTML = '<i class="fa fa-stop"></i>';
    } catch (error) {
      console.error("Audioaufnahme fehlgeschlagen:", error);
    }
  }

  private stopRecording(): void {
    this.mediaRecorder?.stop();
    this.isRecording = false;
    this.button.innerHTML = '<i class="fa fa-microphone"></i>';
  }
}

class AudioVisualizer {
  private canvas: HTMLCanvasElement;
  private canvasCtx: CanvasRenderingContext2D;
  private audioCtx: AudioContext;
  private analyser: AnalyserNode;
  private dataArray: Uint8Array;
  private animationFrameId: number | null = null;
  private source: MediaStreamAudioSourceNode | null = null;

  constructor() {
    this.canvas = document.createElement("canvas");
    this.canvas.width = 64;
    this.canvas.height = 30;

    // this.canvas.style.width = "100%";
    // this.canvas.style.maxWidth = "100px";
    // this.canvas.style.height = "32px";

    // window.addEventListener("resize", this.resizeCanvas.bind(this));
    // this.resizeCanvas();

    const ctx = this.canvas.getContext("2d");
    if (!ctx) {
      throw new Error("Canvas-Kontext konnte nicht abgerufen werden.");
    }
    this.canvasCtx = ctx;

    this.audioCtx = new AudioContext();
    this.analyser = this.audioCtx.createAnalyser();
    this.analyser.fftSize = 256;

    const bufferLength = this.analyser.frequencyBinCount;
    this.dataArray = new Uint8Array(bufferLength);
  }

  public getCanvasElement(): HTMLCanvasElement {
    return this.canvas;
  }

  // private resizeCanvas() {
  //   const rect = this.canvas.getBoundingClientRect();
  //   this.canvas.width = rect.width;
  //   this.canvas.height = rect.height;
  // }

  public start(stream: MediaStream): void {
    this.source = this.audioCtx.createMediaStreamSource(stream);
    this.source.connect(this.analyser);

    const barCount = 7;
    const color = "#8c0035";

    const draw = () => {
      this.analyser.getByteFrequencyData(this.dataArray);
      this.analyser.smoothingTimeConstant = 0.8;

      this.canvasCtx.clearRect(0, 0, this.canvas.width, this.canvas.height);

      const barWidth = this.canvas.width / barCount;
      const step = Math.floor(this.dataArray.length / barCount);

      for (let i = 0; i < barCount; i++) {
        const value = this.dataArray[i * step];
        const barHeight = (value / 255) * this.canvas.height;

        this.canvasCtx.fillStyle = color;
        this.canvasCtx.beginPath();
        this.canvasCtx.roundRect(
          i * barWidth,
          this.canvas.height - barHeight,
          barWidth - 2,
          barHeight,
          [4, 4, 4, 4]
        );
        this.canvasCtx.fill();
      }

      this.animationFrameId = requestAnimationFrame(draw);
    };

    draw();
  }

  public stop(): void {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    this.source?.disconnect();
    this.analyser.disconnect();
    this.canvasCtx.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }
}
